import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle2,
  FolderTree,
  KeyRound,
  Loader2,
  Pencil,
  Plug,
  Plus,
  Search,
  Server as ServerIcon,
  Trash2,
  X,
  XCircle,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useSubscriptionAccess } from '@/hooks/use-subscription-access';
import { getErrorMessage } from '@/lib/errors';
import { serversApi } from '@/lib/servers-api';
import { countOf } from '@/lib/trial';
import { cn } from '@/lib/utils';
import { CredentialFormDialog } from '@/modules/servers/CredentialFormDialog';
import { GroupFormDialog } from '@/modules/servers/GroupFormDialog';
import { SubscriptionNotice } from '@/modules/billing/SubscriptionNotice';
import { ServerFormDialog } from '@/modules/servers/ServerFormDialog';
import { TrustHostKey } from '@/modules/servers/TrustHostKey';
import { hardwareSummary, privilegeBadge, privilegeProblem } from '@/modules/servers/describe';
import type { Server, ServerGroup, ServerStatus, SshCredential } from '@/types/api';

const statusTone: Record<ServerStatus, 'success' | 'warning' | 'danger' | 'muted'> = {
  online: 'success',
  unverified: 'muted',
  offline: 'warning',
  unreachable: 'danger',
  unsupported_os: 'warning',
};

const STATUS_FILTERS: { value: ServerStatus | ''; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'online', label: 'Online' },
  { value: 'unverified', label: 'Unverified' },
  { value: 'offline', label: 'Offline' },
  { value: 'unreachable', label: 'Unreachable' },
  { value: 'unsupported_os', label: 'Unsupported OS' },
];

type Tab = 'servers' | 'credentials' | 'groups';

/**
 * What the last connection test on one server found, said on that server's row.
 *
 * `connected` and `failed` are both answers from the server: the check ran, and the record it
 * returned says whether it connected. `error` is the request itself failing — the subscription, the
 * network, the platform — which says nothing about the machine.
 */
type TestOutcome =
  | { kind: 'connected'; server: Server }
  | { kind: 'failed'; server: Server }
  | { kind: 'error'; message: string };

/** How long "Connected" stays on a row. A failure stays until it is dismissed or tested again. */
const CONNECTED_MESSAGE_MS = 10_000;

/** What a pending destructive confirmation refers to. */
type PendingDelete =
  | { kind: 'server'; id: string; label: string }
  | { kind: 'credential'; id: string; label: string; inUse: number }
  | { kind: 'group'; id: string; label: string; members: number };

export function ServersPage() {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>('servers');

  const [status, setStatus] = useState<ServerStatus | ''>('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const [serverForm, setServerForm] = useState<{ open: boolean; server: Server | null }>({
    open: false,
    server: null,
  });
  const [credentialForm, setCredentialForm] = useState<{ open: boolean; credential: SshCredential | null }>({
    open: false,
    credential: null,
  });
  const [groupForm, setGroupForm] = useState<{ open: boolean; group: ServerGroup | null }>({
    open: false,
    group: null,
  });
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null);

  const inventory = useQuery({ queryKey: ['server-inventory'], queryFn: serversApi.inventory });
  const servers = useQuery({
    queryKey: ['servers', status, search],
    queryFn: () => serversApi.list({ status, search }),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['servers'] });
    void queryClient.invalidateQueries({ queryKey: ['server-inventory'] });
  };

  /*
   * Testing a connection says what it found, on the row that was tested.
   *
   * It used to refresh the list and say nothing: a server already online stayed online, so a test
   * that had just succeeded looked exactly like a button that did nothing. And a message at the top
   * of the page would be out of sight for anyone who scrolled down to the row they clicked.
   *
   * Kept per server, so two tests running at once each report on their own row, and a slow one
   * finishing late cannot overwrite the answer for a server tested after it.
   */
  const [testing, setTesting] = useState<ReadonlySet<string>>(new Set());
  const [testOutcomes, setTestOutcomes] = useState<Record<string, TestOutcome>>({});

  const forgetOutcome = (id: string, only?: TestOutcome) =>
    setTestOutcomes((current) => {
      if (!(id in current) || (only !== undefined && current[id] !== only)) {
        return current;
      }

      const next = { ...current };
      delete next[id];

      return next;
    });

  const test = useMutation({
    mutationFn: (id: string) => serversApi.test(id),
    onMutate: (id) => {
      forgetOutcome(id);
      setTesting((current) => new Set(current).add(id));
    },
    onSuccess: (server, id) => {
      // The check ran either way; the record says whether it connected. An unsupported
      // distribution still connected — commands run there, only playbooks do not.
      const outcome: TestOutcome =
        server.status === 'online' || server.status === 'unsupported_os'
          ? { kind: 'connected', server }
          : { kind: 'failed', server };

      setTestOutcomes((current) => ({ ...current, [id]: outcome }));

      if (outcome.kind === 'connected') {
        window.setTimeout(() => forgetOutcome(id, outcome), CONNECTED_MESSAGE_MS);
      }

      invalidate();
    },
    onError: (error, id) => {
      setTestOutcomes((current) => ({ ...current, [id]: { kind: 'error', message: getErrorMessage(error) } }));
    },
    onSettled: (_data, _error, id) =>
      setTesting((current) => {
        const next = new Set(current);
        next.delete(id);

        return next;
      }),
  });
  const bulkTest = useMutation({
    mutationFn: (ids: string[]) => serversApi.bulk({ action: 'test_connection', server_ids: ids }),
    onSuccess: () => {
      setSelected(new Set());
      invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: (target: PendingDelete) => {
      if (target.kind === 'server') return serversApi.remove(target.id);
      if (target.kind === 'credential') return serversApi.removeCredential(target.id);
      return serversApi.removeGroup(target.id);
    },
    onSuccess: () => {
      setPendingDelete(null);
      invalidate();
    },
  });

  // The record returned by the last save. Saving verifies the connection, so this is how
  // a server that saved but could not be reached gets reported, rather than disappearing
  // behind a dialog that closed as if all was well.
  const [saveOutcome, setSaveOutcome] = useState<Server | null>(null);

  // Provisioning is gated on an active subscription; the API refuses with 402 regardless,
  // this just stops a customer filling in a form that cannot be submitted.
  const { permitsChanges: subscriptionPermitsChanges, trial } = useSubscriptionAccess();
  const onTrial = trial?.status === 'running';

  // Fetched only while the confirmation is open, and only for a server -- credentials and groups
  // carry their own counts already.
  const pendingServerId = pendingDelete?.kind === 'server' ? pendingDelete.id : null;

  const dependents = useQuery({
    queryKey: ['server-dependents', pendingServerId],
    queryFn: () => serversApi.dependents(pendingServerId ?? ''),
    enabled: pendingServerId !== null,
  });

  // A single test reports on its own row; this is for the actions that have no row to report on.
  const actionError = bulkTest.error ?? remove.error;
  const quota = inventory.data?.quota;
  // Taken from the API rather than re-derived from the numbers: the server already applies
  // this rule on create, and staff have no plan to be measured against at all.
  const atQuota = quota !== undefined && !quota.permits_more;
  const credentials = inventory.data?.credentials ?? [];
  const groups = inventory.data?.groups ?? [];

  function toggle(id: string) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <div className="space-y-6">
      {/* The button sits at the bottom right of the header at every width. The description used to
          have no width of its own, so it claimed the whole row and pushed the button onto a line
          of its own at the left. */}
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight">Servers</h1>
          <p className="max-w-3xl text-sm text-muted-foreground">
            Every machine automation, monitoring and backups can reach. Testing a connection refreshes
            what the platform knows about it: operating system, hardware, and how it can act as root.
          </p>
        </div>
        <div className="ml-auto flex items-center gap-4">
          {quota && (
            <div className="text-right text-sm">
              <p className="font-medium tabular-nums">
                {quota.in_use}
                {quota.unlimited ? '' : ` / ${quota.limit}`}
              </p>
              <p className="text-xs text-muted-foreground">
                {quota.unlimited ? 'Unlimited servers' : `${quota.remaining ?? 0} remaining`}
              </p>
            </div>
          )}
          <Button
            onClick={() => setServerForm({ open: true, server: null })}
            disabled={atQuota || !subscriptionPermitsChanges}
          >
            <Plus /> Add server
          </Button>
        </div>
      </div>

      <SubscriptionNotice />

      {atQuota && quota && (
        <Alert variant="warning">
          {onTrial
            ? `The free trial includes ${countOf(quota.limit, 'server', 'servers')}, and ${quota.limit === 1 ? 'it is' : 'all of them are'} in use. Choose a plan to connect more, or remove a server to connect a different one.`
            : 'You have reached your plan’s server limit. Upgrade your plan or remove a server to add another.'}{' '}
          <Link to="/billing" className="font-medium underline underline-offset-2">
            {onTrial ? 'See plans' : 'Go to billing'}
          </Link>
          .
        </Alert>
      )}
      {actionError && <Alert variant="danger">{getErrorMessage(actionError)}</Alert>}

      {/* No invented fallback message: the API always supplies a reason for a non-online
          status, so anything missing here is a backend defect that must stay visible rather
          than be papered over with "could not be verified". */}
      {saveOutcome && saveOutcome.status !== 'online' && saveOutcome.last_error && (
        <Alert variant="warning">
          <strong>{saveOutcome.name}</strong> was saved, but the connection did not succeed:{' '}
          {saveOutcome.last_error}
        </Alert>
      )}
      {saveOutcome && saveOutcome.status === 'unverified' && !saveOutcome.last_error && (
        <Alert variant="warning">
          <strong>{saveOutcome.name}</strong> was saved but has not been checked yet. It has no
          SSH credential attached. Add one, then use Test connection.
        </Alert>
      )}
      {saveOutcome && saveOutcome.status === 'online' && (
        <Alert variant="success">
          <strong>{saveOutcome.name}</strong> is connected
          {saveOutcome.os_label ? ` and running ${saveOutcome.os_label}` : ''}
          {saveOutcome.architecture_label ? ` on ${saveOutcome.architecture_label}` : ''}.
        </Alert>
      )}

      {bulkTest.data && (
        <Alert variant="info">
          Queued {bulkTest.data.queued_count} connection test
          {bulkTest.data.queued_count === 1 ? '' : 's'}. Statuses
          update here as each finishes.
          {bulkTest.data.skipped_count > 0 &&
            ` ${bulkTest.data.skipped_count} server${bulkTest.data.skipped_count === 1 ? ' was' : 's were'} skipped.`}
        </Alert>
      )}

      <nav className="scrollbar-none flex gap-1 overflow-x-auto border-b border-border pb-px">
        <TabButton active={tab === 'servers'} onClick={() => setTab('servers')} icon={ServerIcon}>
          Servers
        </TabButton>
        <TabButton active={tab === 'credentials'} onClick={() => setTab('credentials')} icon={KeyRound}>
          Credentials ({credentials.length})
        </TabButton>
        <TabButton active={tab === 'groups'} onClick={() => setTab('groups')} icon={FolderTree}>
          Groups ({groups.length})
        </TabButton>
      </nav>

      {tab === 'servers' && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Filter</CardTitle>
              <CardDescription>Search by name, address, or reported hostname.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-1">
                {STATUS_FILTERS.map((filter) => (
                  <Button
                    key={filter.label}
                    variant={status === filter.value ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setStatus(filter.value)}
                  >
                    {filter.label}
                  </Button>
                ))}
              </div>

              <form
                className="flex flex-wrap items-end gap-3"
                onSubmit={(e) => {
                  e.preventDefault();
                  setSearch(searchInput);
                }}
              >
                <div className="min-w-56 flex-1 space-y-2">
                  <Label htmlFor="server-search">Search</Label>
                  <Input
                    id="server-search"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />
                </div>
                <Button type="submit" variant="outline">
                  <Search /> Search
                </Button>
              </form>
            </CardContent>
          </Card>

          {selected.size > 0 && (
            <Card>
              <CardContent className="flex flex-wrap items-center justify-between gap-3 py-4">
                <p className="text-sm">
                  <span className="font-medium tabular-nums">{selected.size}</span> selected
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => bulkTest.mutate([...selected])}
                    disabled={bulkTest.isPending || !subscriptionPermitsChanges}
                  >
                    {bulkTest.isPending ? <Loader2 className="animate-spin" /> : <Plug />} Test
                    connections
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>
                    Clear
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Inventory</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {servers.isLoading && (
                <p className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="size-4 animate-spin" /> Loading servers…
                </p>
              )}
              {servers.isError && <Alert variant="danger">{getErrorMessage(servers.error)}</Alert>}

              {servers.data?.data.length === 0 && (
                <EmptyState
                  icon={ServerIcon}
                  title="No servers yet"
                  body="Add a server with its address and an SSH key or password. The platform connects, detects the operating system, and checks whether the account is root or can use sudo."
                  action={
                    // Gated like the header button: a customer with no entitlement has
                    // nothing to add, and offering the form only to reject the submission
                    // is worse than saying so up front.
                    <Button
                      onClick={() => setServerForm({ open: true, server: null })}
                      disabled={atQuota || !subscriptionPermitsChanges}
                    >
                      <Plus /> Add server
                    </Button>
                  }
                />
              )}

              {servers.data?.data.map((server) => (
                <ServerRow
                  key={server.id}
                  server={server}
                  selected={selected.has(server.id)}
                  onToggle={() => toggle(server.id)}
                  permitsChanges={subscriptionPermitsChanges}
                  onTest={() => test.mutate(server.id)}
                  onEdit={() => setServerForm({ open: true, server })}
                  onDelete={() =>
                    setPendingDelete({ kind: 'server', id: server.id, label: server.name })
                  }
                  testing={testing.has(server.id)}
                  outcome={testOutcomes[server.id]}
                  onDismissOutcome={() => forgetOutcome(server.id)}
                />
              ))}
            </CardContent>
          </Card>
        </>
      )}

      {tab === 'credentials' && (
        <Card>
          <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
            <div>
              <CardTitle>SSH credentials</CardTitle>
              <CardDescription>
                Stored encrypted and reusable across servers. Secrets are never shown again.
              </CardDescription>
            </div>
            <Button size="sm" onClick={() => setCredentialForm({ open: true, credential: null })}>
              <Plus /> Add credential
            </Button>
          </CardHeader>
          <CardContent className="space-y-3">
            {credentials.length === 0 && (
              <EmptyState
                icon={KeyRound}
                title="No credentials yet"
                body="Add the SSH key or password the platform should use to reach your servers."
              />
            )}

            {credentials.map((credential) => (
              <div
                key={credential.id}
                className="flex flex-col gap-3 rounded-md border border-border bg-muted/20 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="flex min-w-0 flex-1 items-start gap-3">
                  <KeyRound className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{credential.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {credential.auth_type_label} · {credential.username}
                      {credential.servers_using > 0 &&
                        ` · used by ${credential.servers_using} server${credential.servers_using === 1 ? '' : 's'}`}
                    </p>
                    {credential.fingerprint && (
                      <p className="truncate font-mono text-[11px] text-muted-foreground">
                        {credential.fingerprint}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2 self-end sm:self-auto">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCredentialForm({ open: true, credential })}
                  >
                    <Pencil /> Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-danger"
                    onClick={() =>
                      setPendingDelete({
                        kind: 'credential',
                        id: credential.id,
                        label: credential.name,
                        inUse: credential.servers_using,
                      })
                    }
                  >
                    <Trash2 />
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {tab === 'groups' && (
        <Card>
          <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
            <div>
              <CardTitle>Groups</CardTitle>
              <CardDescription>
                Organise servers by environment or role. Deleting a group never deletes its servers.
              </CardDescription>
            </div>
            <Button size="sm" onClick={() => setGroupForm({ open: true, group: null })}>
              <Plus /> Add group
            </Button>
          </CardHeader>
          <CardContent className="space-y-3">
            {groups.length === 0 && (
              <EmptyState icon={FolderTree} title="No groups yet" body="Create a group to organise your servers." />
            )}

            {groups.map((group) => (
              <div
                key={group.id}
                className="flex flex-col gap-3 rounded-md border border-border bg-muted/20 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{group.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {group.server_count} server{group.server_count === 1 ? '' : 's'}
                    {group.description && ` · ${group.description}`}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2 self-end sm:self-auto">
                  <Button variant="ghost" size="sm" onClick={() => setGroupForm({ open: true, group })}>
                    <Pencil /> Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-danger"
                    onClick={() =>
                      setPendingDelete({
                        kind: 'group',
                        id: group.id,
                        label: group.name,
                        members: group.server_count,
                      })
                    }
                  >
                    <Trash2 />
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <ServerFormDialog
        open={serverForm.open}
        onOpenChange={(open) => setServerForm((c) => ({ ...c, open }))}
        server={serverForm.server}
        credentials={credentials}
        groups={groups}
        onSaved={setSaveOutcome}
      />
      <CredentialFormDialog
        open={credentialForm.open}
        onOpenChange={(open) => setCredentialForm((c) => ({ ...c, open }))}
        credential={credentialForm.credential}
      />
      <GroupFormDialog
        open={groupForm.open}
        onOpenChange={(open) => setGroupForm((c) => ({ ...c, open }))}
        group={groupForm.group}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title={pendingDelete ? `Delete ${pendingDelete.kind} “${pendingDelete.label}”?` : ''}
        description={describeDeletion(pendingDelete, dependents.data?.items ?? [])}
        confirmLabel={pendingDelete ? `Delete ${pendingDelete.kind}` : 'Delete'}
        busy={remove.isPending}
        error={remove.error}
        onConfirm={() => pendingDelete && remove.mutate(pendingDelete)}
      />
    </div>
  );
}

/** Says what will actually happen, including the cases the server will refuse. */
function describeDeletion(
  target: PendingDelete | null,
  dependents: { label: string; count: number }[],
): string {
  if (target === null) return '';

  if (target.kind === 'server') {
    // ## Naming what goes with it is the whole point
    //
    // Deleting a server used to be refused while anything was attached, and the customer was told
    // to remove everything on it first. That protected nothing -- forgetting a record does not
    // uninstall anything either -- so it was several "forget" operations to earn one more.
    //
    // It cascades now, which is only fair if the confirmation says so in numbers rather than
    // leaving somebody to discover afterwards what else disappeared.
    const attached = dependents
      .map((item) => `${item.count} ${item.count === 1 ? item.label.replace(/s$/, '') : item.label}`)
      .join(' and ');

    if (attached === '') {
      return 'The server is removed from your inventory. Nothing is changed on the machine itself.';
    }

    return (
      `This also removes ${attached} from the platform. Nothing is uninstalled: the files, `
      + 'services and databases stay exactly where they are on the machine, still running.'
    );
  }

  if (target.kind === 'credential') {
    return target.inUse > 0
      ? `This credential is used by ${target.inUse} server${target.inUse === 1 ? '' : 's'}. Reassign them first. The deletion will be refused otherwise.`
      : 'The stored key or password is permanently deleted. This cannot be undone.';
  }

  return target.members > 0
    ? `${target.members} server${target.members === 1 ? '' : 's'} will become ungrouped. No servers are deleted.`
    : 'The group is removed.';
}

function TabButton({
  active,
  onClick,
  icon: Icon,
  children,
}: {
  active: boolean;
  onClick: () => void;
  icon: typeof ServerIcon;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-selected={active}
      role="tab"
      className={cn(
        'flex items-center gap-2 whitespace-nowrap border-b-2 px-4 py-2.5 text-sm transition-colors',
        active
          ? 'border-primary text-foreground'
          : 'border-transparent text-muted-foreground hover:text-foreground',
      )}
    >
      <Icon className="size-4" />
      {children}
    </button>
  );
}

function EmptyState({
  icon: Icon,
  title,
  body,
  action,
}: {
  icon: typeof ServerIcon;
  title: string;
  body: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-2 py-12 text-center">
      <Icon className="size-8 text-muted-foreground" />
      <p className="font-medium">{title}</p>
      <p className="max-w-sm text-sm text-muted-foreground">{body}</p>
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

function ServerRow({
  server,
  selected,
  onToggle,
  onTest,
  onEdit,
  onDelete,
  testing,
  outcome,
  onDismissOutcome,
  permitsChanges,
}: {
  server: Server;
  selected: boolean;
  onToggle: () => void;
  onTest: () => void;
  onEdit: () => void;
  onDelete: () => void;
  testing: boolean;
  /** What the last test of this server found, while it is still worth saying. */
  outcome: TestOutcome | undefined;
  onDismissOutcome: () => void;
  /** Testing and editing are gated on an active subscription; deleting deliberately is not. */
  permitsChanges: boolean;
}) {
  const privilege = privilegeBadge(server);
  const problem = privilegeProblem(server);
  const hardware = hardwareSummary(server);

  return (
    // Two columns that never wrap into each other from a small screen up. The row used to be one
    // wrapping flex line, so a server whose details ran longer than the others' (a longer
    // distribution name, a bigger disk) dropped its badges and buttons onto a line of their own
    // under the text. Now the details wrap inside their column and the controls stay on the right.
    <div
      className={cn(
        'flex flex-col gap-3 rounded-md border px-4 py-3 transition-colors sm:flex-row sm:items-center sm:justify-between sm:gap-6',
        selected ? 'border-primary/60 bg-primary/10' : 'border-border bg-muted/20',
      )}
    >
      <div className="flex min-w-0 flex-1 items-start gap-3">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          aria-label={`Select ${server.name}`}
          className="mt-1 size-4 accent-primary"
        />
        <div className="min-w-0">
          <Link to={`/servers/${server.id}`} className="block truncate text-sm font-medium hover:text-primary hover:underline">
            {server.name}
          </Link>
          <p className="truncate font-mono text-xs text-muted-foreground">
            {server.ssh_user}@{server.endpoint}
            {server.hostname !== '' && server.hostname !== server.name && ` · ${server.hostname}`}
          </p>
          <p className="text-xs text-muted-foreground">
            {server.os_label ?? 'Operating system not yet detected'}
            {/* All detected on connect. Shown together because they are what a playbook
                picks its packages by. */}
            {server.architecture_label && ` · ${server.architecture_label}`}
            {server.package_manager && ` · ${server.package_manager}`}
            {hardware.length > 0 && ` · ${hardware.join(' · ')}`}
            {server.last_checked_at &&
              ` · checked ${new Date(server.last_checked_at).toLocaleString()}`}
          </p>
          {server.os_label !== null && !server.os_supported && (
            <p className="mt-1 text-xs text-warning">
              Playbooks and templates are not written for this distribution. Commands still run.
            </p>
          )}
          {problem !== null && <p className="mt-1 text-xs text-warning">{problem}</p>}
          {/* A failed test says the reason in its own message, so it is not repeated under it. */}
          {server.last_error && outcome?.kind !== 'failed' && (
            <p className="mt-1 text-xs text-danger">{server.last_error}</p>
          )}
          {server.tags.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {server.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded border border-border px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
          <div aria-live="polite">
            {outcome !== undefined && <TestMessage outcome={outcome} onDismiss={onDismissOutcome} />}
          </div>
          {/* Under the error it answers. Trusting drops what the failed test said about this row. */}
          <TrustHostKey server={server} onTrusted={onDismissOutcome} className="mt-2" />
        </div>
      </div>

      {/* On a phone, one line under the details: the badges at the left, the buttons at the right,
          with "Test" as its icon alone so all five fit. */}
      <div className="flex shrink-0 items-center justify-between gap-2 sm:justify-end">
        <div className="flex items-center gap-2">
          <Badge variant={privilege.tone} title="How the platform can act as root on this server">
            {privilege.label}
          </Badge>
          <Badge variant={statusTone[server.status]}>{server.status_label}</Badge>
        </div>
        <div className="flex items-center gap-1 sm:gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onTest}
            disabled={testing || !permitsChanges}
            aria-label={`Test the connection to ${server.name}`}
            title="Test the connection"
          >
            {testing ? <Loader2 className="animate-spin" /> : <Plug />}
            <span className="hidden sm:inline">Test</span>
          </Button>
          {/* Editing is a gated route (servers.update); deleting below is not, so nobody is
              trapped paying for a resource they cannot remove. */}
          <Button
            variant="ghost"
            size="sm"
            onClick={onEdit}
            disabled={!permitsChanges}
            aria-label={`Edit ${server.name}`}
            title="Edit"
          >
            <Pencil />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-danger"
            onClick={onDelete}
            aria-label={`Delete ${server.name}`}
            title="Delete"
          >
            <Trash2 />
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * The answer to "Test", in a sentence.
 *
 * Connected says what was found, because that is what a test refreshes: the distribution and how the
 * platform reaches root. A failure gives the reason the server returned; a request that never reached
 * the check says that instead, so a lapsed subscription is not mistaken for an unreachable machine.
 */
function TestMessage({ outcome, onDismiss }: { outcome: TestOutcome; onDismiss: () => void }) {
  const connected = outcome.kind === 'connected';

  let text: string;

  if (outcome.kind === 'connected') {
    const found = [outcome.server.os_label, outcome.server.privilege_label].filter(
      (part): part is string => part !== null && part !== '',
    );
    text = `Connected${found.length > 0 ? `: ${found.join(' · ')}` : ''}.`;

    if (outcome.server.status === 'unsupported_os') {
      text += ' Playbooks are not written for this distribution; commands still run.';
    }
  } else if (outcome.kind === 'failed') {
    text = `Connection failed: ${outcome.server.last_error ?? outcome.server.status_label}`;
  } else {
    text = `The test could not run: ${outcome.message}`;
  }

  return (
    // Announced by the live region the row keeps around it, which has to exist before the message
    // does for a screen reader to notice the change.
    <p
      className={cn(
        'mt-2 inline-flex max-w-full items-start gap-1.5 rounded-md py-1 pl-2 pr-1 text-xs',
        connected ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger',
      )}
    >
      {connected ? (
        <CheckCircle2 className="mt-px size-3.5 shrink-0" aria-hidden />
      ) : (
        <XCircle className="mt-px size-3.5 shrink-0" aria-hidden />
      )}
      <span className="min-w-0">{text}</span>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="-my-0.5 shrink-0 rounded p-0.5 opacity-70 transition-opacity hover:opacity-100"
      >
        <X className="size-3" aria-hidden />
      </button>
    </p>
  );
}
